import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  User, FileText, Briefcase, Upload, AlertTriangle, 
  MapPin, Building, Calendar, Award, ExternalLink,
  Search, CheckCircle, XCircle, Pause, RefreshCw, Save,
  Send, Clock, FileSearch, Waves, GraduationCap
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import ZenButton from '../../components/ZenButton';

const STATUTS_EMPLOI = [
  { value: "en_recherche", label: "En recherche d'emploi", icon: Search, color: "#3B82F6" },
  { value: "en_emploi", label: "En emploi", icon: Briefcase, color: "#22C55E" },
  { value: "autre_emploi", label: "Autre emploi", icon: RefreshCw, color: "#8B5CF6" },
  { value: "arret", label: "Arrêt / Maladie / Maternité", icon: Pause, color: "#EF4444" },
];

const TYPES_CONTRAT = [
  { value: "cdi", label: "CDI" },
  { value: "cdd", label: "CDD" },
  { value: "saisonnier", label: "Saisonnier" },
  { value: "interim", label: "Intérim" },
  { value: "autre", label: "Autre" },
];

const DashboardMoniteur = ({ user }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showStatutDialog, setShowStatutDialog] = useState(false);
  const [showDocumentDialog, setShowDocumentDialog] = useState(false);
  const [showAnnoncesDialog, setShowAnnoncesDialog] = useState(false);
  const [annonces, setAnnonces] = useState([]);
  const [loadingAnnonces, setLoadingAnnonces] = useState(false);
  
  const [statutData, setStatutData] = useState({
    statut: '',
    lieu: '',
    structure: '',
    type_contrat: '',
    date_debut: '',
    date_fin: ''
  });
  
  const [documentData, setDocumentData] = useState({
    nom: '',
    fichier: ''
  });

  const [selectedAnnonce, setSelectedAnnonce] = useState(null);
  const [candidatureComment, setCandidatureComment] = useState('');
  const [sendingCandidature, setSendingCandidature] = useState(false);

  useEffect(() => {
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProfile = async () => {
    try {
      const response = await axios.get('/moniteur/profile');
      setProfile(response.data);
      setStatutData({
        statut: response.data.statut_emploi || 'en_recherche',
        lieu: response.data.emploi_lieu || '',
        structure: response.data.emploi_structure || '',
        type_contrat: response.data.emploi_type_contrat || '',
        date_debut: response.data.emploi_date_debut || '',
        date_fin: response.data.emploi_date_fin || ''
      });
    } catch (error) {
      toast.error('Erreur lors du chargement du profil');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatut = async () => {
    setSaving(true);
    try {
      await axios.put('/moniteur/statut-emploi', statutData);
      await loadProfile();
      setShowStatutDialog(false);
      toast.success('Statut emploi mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = (field, event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (file.size > 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 1 Mo)');
      return;
    }
    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64 = e.target.result;
      try {
        await axios.put('/moniteur/profile', { [field]: base64 });
        setProfile(prev => ({ ...prev, [field]: base64 }));
        toast.success('Document ajouté');
      } catch (error) {
        toast.error("Erreur lors de l'upload");
      }
    };
    reader.readAsDataURL(file);
  };

  const handleAddDocument = async () => {
    if (!documentData.nom || !documentData.fichier) {
      toast.error('Nom et fichier requis');
      return;
    }
    setSaving(true);
    try {
      await axios.post('/moniteur/document', documentData);
      await loadProfile();
      setShowDocumentDialog(false);
      setDocumentData({ nom: '', fichier: '' });
      toast.success('Document ajouté');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'ajout");
    } finally {
      setSaving(false);
    }
  };

  const handleDocumentFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (file.size > 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 1 Mo)');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setDocumentData(prev => ({ ...prev, fichier: e.target.result }));
    };
    reader.readAsDataURL(file);
  };

  const loadAnnonces = async () => {
    setLoadingAnnonces(true);
    try {
      const response = await axios.get('/offres-emploi/actives');
      setAnnonces(response.data || []);
    } catch (error) {
      setAnnonces([]);
    } finally {
      setLoadingAnnonces(false);
    }
  };

  const handleOpenAnnonces = () => {
    setShowAnnoncesDialog(true);
    loadAnnonces();
  };

  const handlePostuler = async () => {
    if (!selectedAnnonce) return;
    setSendingCandidature(true);
    try {
      await axios.post(`/offres-emploi/${selectedAnnonce.offre_id}/postuler`, {
        commentaire: candidatureComment
      });
      toast.success('Candidature envoyée avec succès !');
      setSelectedAnnonce(null);
      setCandidatureComment('');
      loadAnnonces();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSendingCandidature(false);
    }
  };

  const getStatutInfo = (statut) => {
    return STATUTS_EMPLOI.find(s => s.value === statut) || STATUTS_EMPLOI[0];
  };

  const getTypeContratLabel = (type) => {
    const found = TYPES_CONTRAT.find(t => t.value === type);
    return found ? found.label : type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-[#8DC63F]" />
      </div>
    );
  }

  const statutInfo = getStatutInfo(profile?.statut_emploi);
  const StatutIcon = statutInfo.icon;

  const DOC_FIELDS = [
    { key: 'cv_document', label: 'CV', accept: '.pdf,.doc,.docx' },
    { key: 'carte_pro_document', label: 'Carte Professionnelle', accept: '.pdf,.jpg,.jpeg,.png' },
    { key: 'diplome_document', label: 'Diplôme BPJEPS', accept: '.pdf,.jpg,.jpeg,.png' },
    { key: 'permis_document', label: 'Permis', accept: '.pdf,.jpg,.jpeg,.png' },
    { key: 'crr_document', label: 'CRR', accept: '.pdf,.jpg,.jpeg,.png' },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-moniteur">
      {/* Bandeau Bleu - Header */}
      <div className="bg-gradient-to-r from-[#1E40AF] to-[#3B82F6] rounded-xl p-6 text-white shadow-lg">
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          <div className="flex-shrink-0 relative group">
            <div className="w-24 h-24 bg-white/20 border-4 border-white/40 rounded-xl overflow-hidden flex items-center justify-center">
              {profile?.photo_identite ? (
                <img src={profile.photo_identite} alt="Photo" className="w-full h-full object-cover" />
              ) : (
                <User className="h-10 w-10 text-white/60" />
              )}
            </div>
            <input
              type="file"
              id="upload-photo"
              accept=".jpg,.jpeg,.png"
              onChange={(e) => handleFileUpload('photo_identite', e)}
              className="hidden"
            />
            <button
              onClick={() => document.getElementById('upload-photo').click()}
              className="absolute inset-0 bg-black/40 rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
              title="Changer la photo"
              data-testid="change-photo-btn"
            >
              <Upload className="h-6 w-6 text-white" />
            </button>
          </div>
          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-bold mb-1">
              {profile?.prenom} {profile?.nom}
            </h1>
            <div className="bg-white/10 rounded-lg px-4 py-3 mt-2">
              <p className="font-semibold flex items-center gap-2 mb-1">
                <GraduationCap className="h-5 w-5" />
                Moniteur VNM diplômé - Promotion {profile?.annee_diplome || '2025'}
              </p>
              <p className="text-sm text-white/80">
                BPJEPS Activités du Motonautisme et Disciplines Associées
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg" data-testid="statut-badge">
              <StatutIcon className="h-5 w-5" style={{ color: statutInfo.color }} />
              <div>
                <p className="text-sm font-semibold">{statutInfo.label}</p>
                {profile?.emploi_structure && (
                  <p className="text-xs text-white/70">{profile.emploi_structure}</p>
                )}
              </div>
            </div>
            {profile?.carte_pro_numero && (
              <div className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg">
                <Award className="h-5 w-5 text-amber-300" />
                <p className="text-xs">Carte Pro : {profile.carte_pro_numero}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Liens rapides en haut */}
      <div className="flex flex-wrap gap-3">
        <ZenButton />
        <Button
          onClick={() => window.open('https://recherche-educateur.sports.gouv.fr/accueil', '_blank')}
          className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white shadow-lg"
          data-testid="recherche-educateurs-btn"
        >
          <Search className="h-4 w-4 mr-2" />
          Recherche Éducateurs Sportifs
        </Button>
        <Button
          variant="outline"
          onClick={handleOpenAnnonces}
          className="border-blue-300 text-blue-700 hover:bg-blue-50"
          data-testid="voir-annonces-btn"
        >
          <Briefcase className="h-4 w-4 mr-2" />
          Voir les annonces d'emploi
        </Button>
      </div>

      {/* Alerte carte pro */}
      {(!profile?.carte_pro_date_obtention || profile?.carte_pro_expire_soon) && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-medium text-amber-800">
                  {!profile?.carte_pro_date_obtention 
                    ? "Carte professionnelle à obtenir"
                    : `Carte professionnelle expire dans ${profile.carte_pro_days_remaining} jours`
                  }
                </h3>
                <p className="text-sm text-amber-700 mt-1">
                  N'oubliez pas de vous déclarer sur le site officiel. Elle est valable 5 ans et nécessite : photo, pièce d'identité, diplômes, certificat médical.
                </p>
                <Button 
                  variant="outline" 
                  className="mt-3 border-amber-400 text-amber-700 hover:bg-amber-100"
                  onClick={() => window.open('https://declaration-educateur.sports.gouv.fr', '_blank')}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Déclarer sur le site officiel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Carte Pro Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="h-5 w-5 text-[#8DC63F]" />
            Carte Professionnelle
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Numéro de carte</Label>
              <Input 
                value={profile?.carte_pro_numero || ''} 
                onChange={(e) => setProfile({...profile, carte_pro_numero: e.target.value})}
                onBlur={(e) => {
                  axios.put('/moniteur/profile', { carte_pro_numero: e.target.value })
                    .then(() => toast.success('Mis à jour'))
                    .catch(() => toast.error('Erreur'));
                }}
                placeholder="Ex: 01723..."
                data-testid="input-carte-pro-numero"
              />
            </div>
            <div>
              <Label>Date d'obtention</Label>
              <Input 
                type="date"
                value={profile?.carte_pro_date_obtention || ''} 
                onChange={(e) => setProfile({...profile, carte_pro_date_obtention: e.target.value})}
                onBlur={(e) => {
                  axios.put('/moniteur/profile', { carte_pro_date_obtention: e.target.value })
                    .then(() => toast.success('Mis à jour'))
                    .catch(() => toast.error('Erreur'));
                }}
                data-testid="input-carte-pro-date"
              />
            </div>
            <div>
              <Label>Date d'expiration</Label>
              <Input 
                value={profile?.carte_pro_date_expiration || 'Calculée automatiquement (+5 ans)'} 
                disabled 
                className="bg-slate-50"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <Label>Années d'expérience</Label>
              <Input 
                type="number"
                min="0"
                value={profile?.annees_experience ?? ''} 
                onChange={(e) => setProfile({...profile, annees_experience: e.target.value})}
                onBlur={(e) => {
                  const val = e.target.value ? parseInt(e.target.value) : null;
                  axios.put('/moniteur/profile', { annees_experience: val })
                    .then(() => toast.success('Mis à jour'))
                    .catch(() => toast.error('Erreur'));
                }}
                placeholder="Ex: 3"
                data-testid="input-annees-experience"
              />
            </div>
            <div>
              <Label>Dernier employeur dans le motonautisme</Label>
              <Input 
                value={profile?.dernier_employeur_motonautisme || ''} 
                onChange={(e) => setProfile({...profile, dernier_employeur_motonautisme: e.target.value})}
                onBlur={(e) => {
                  axios.put('/moniteur/profile', { dernier_employeur_motonautisme: e.target.value })
                    .then(() => toast.success('Mis à jour'))
                    .catch(() => toast.error('Erreur'));
                }}
                placeholder="Ex: NauticPro, OceanSport..."
                data-testid="input-dernier-employeur"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Documents */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-[#8DC63F]" />
            Mes Documents
          </CardTitle>
          <Button 
            size="sm"
            onClick={() => setShowDocumentDialog(true)}
            className="bg-[#8DC63F] hover:bg-[#7AB62F]"
            data-testid="btn-add-document"
          >
            <Upload className="h-4 w-4 mr-2" />
            Ajouter un document
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {DOC_FIELDS.map(doc => (
              <div key={doc.key} className="border border-slate-200 rounded-lg p-4 text-center hover:border-[#8DC63F] transition-colors">
                {profile?.[doc.key] ? (
                  <>
                    <CheckCircle className="h-10 w-10 text-green-500 mx-auto mb-2" />
                    <p className="text-sm font-medium text-slate-700 mb-2">{doc.label}</p>
                    <input
                      type="file"
                      id={`upload-${doc.key}`}
                      accept={doc.accept}
                      onChange={(e) => handleFileUpload(doc.key, e)}
                      className="hidden"
                    />
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => document.getElementById(`upload-${doc.key}`).click()}
                      data-testid={`replace-${doc.key}`}
                    >
                      Remplacer
                    </Button>
                  </>
                ) : (
                  <>
                    <Upload className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm font-medium text-slate-500 mb-2">{doc.label}</p>
                    <input
                      type="file"
                      id={`upload-${doc.key}`}
                      accept={doc.accept}
                      onChange={(e) => handleFileUpload(doc.key, e)}
                      className="hidden"
                    />
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => document.getElementById(`upload-${doc.key}`).click()}
                      data-testid={`add-${doc.key}`}
                    >
                      Ajouter
                    </Button>
                  </>
                )}
              </div>
            ))}
          </div>

          {/* Documents complémentaires */}
          {profile?.documents_complementaires?.some(d => d.source !== 'stagiaire') && (
            <div className="mt-6">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Documents complémentaires</h4>
              <div className="space-y-2">
                {profile.documents_complementaires.filter(d => d.source !== 'stagiaire').map((doc, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-slate-400" />
                      <div>
                        <p className="font-medium text-sm">{doc.nom || doc.original_name}</p>
                        <p className="text-xs text-slate-500">
                          {doc.source === 'stagiaire' ? 'Transféré du dossier stagiaire' : `Ajouté le ${new Date(doc.date || doc.uploaded_at).toLocaleDateString('fr-FR')}`}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Documents archivés du dossier stagiaire */}
          {profile?.documents_stagiaire_archives?.length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <Award className="h-4 w-4 text-blue-500" />
                Documents de mon dossier stagiaire
              </h4>
              <div className="space-y-2">
                {profile.documents_stagiaire_archives.map((doc, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-blue-500" />
                      <div>
                        <p className="font-medium text-sm">{doc.original_name || doc.nom}</p>
                        <p className="text-xs text-blue-600">
                          {doc.category} - {new Date(doc.uploaded_at || doc.date).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>
                    {(doc.file_data || doc.document_id) && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        data-testid={`moniteur-doc-download-${idx}`}
                        onClick={async () => {
                          let href = doc.file_data;
                          if (!href) {
                            try {
                              const res = await axios.get(`/documents/download/${doc.document_id}`);
                              const raw = res.data.file_data || '';
                              href = raw.startsWith('data:') ? raw : `data:${res.data.content_type || 'application/octet-stream'};base64,${raw}`;
                            } catch (e) {
                              toast.error('Document introuvable');
                              return;
                            }
                          }
                          const link = document.createElement('a');
                          link.href = href;
                          link.download = doc.original_name || 'document';
                          link.click();
                        }}
                      >
                        Télécharger
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Statut Emploi */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-[#8DC63F]" />
            Mon statut professionnel
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-600 mb-6">
            Sélectionnez votre situation actuelle. Ces informations alimentent le tableau de suivi de l'insertion professionnelle.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {STATUTS_EMPLOI.map(statut => {
              const Icon = statut.icon;
              const isSelected = profile?.statut_emploi === statut.value;
              return (
                <button
                  key={statut.value}
                  onClick={() => {
                    setStatutData({ 
                      ...statutData, 
                      statut: statut.value,
                      ...(statut.value === 'en_recherche' || statut.value === 'arret' 
                        ? { lieu: '', structure: '', type_contrat: '', date_debut: '', date_fin: '' }
                        : {})
                    });
                    if (statut.value === 'en_emploi' || statut.value === 'autre_emploi') {
                      setShowStatutDialog(true);
                    } else {
                      axios.put('/moniteur/statut-emploi', { statut: statut.value })
                        .then(() => { loadProfile(); toast.success('Statut mis à jour'); })
                        .catch(() => toast.error('Erreur'));
                    }
                  }}
                  className={`p-4 rounded-lg border-2 transition-all text-left ${
                    isSelected 
                      ? 'border-[#8DC63F] bg-[#8DC63F]/10' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid={`statut-${statut.value}`}
                >
                  <Icon 
                    className="h-8 w-8 mb-2" 
                    style={{ color: isSelected ? statut.color : '#94A3B8' }} 
                  />
                  <p className={`font-medium ${isSelected ? 'text-slate-900' : 'text-slate-600'}`}>
                    {statut.label}
                  </p>
                </button>
              );
            })}
          </div>

          {(profile?.statut_emploi === 'en_emploi' || profile?.statut_emploi === 'autre_emploi') && 
           (profile?.emploi_lieu || profile?.emploi_structure) && (
            <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
              <p className="font-medium text-green-800 mb-2">
                {profile?.statut_emploi === 'en_emploi' ? 'Emploi actuel :' : 'Autre emploi :'}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-green-700">
                {profile.emploi_structure && (
                  <div className="flex items-center gap-2">
                    <Building className="h-4 w-4" />
                    <span><strong>Structure :</strong> {profile.emploi_structure}</span>
                  </div>
                )}
                {profile.emploi_lieu && (
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    <span><strong>Lieu :</strong> {profile.emploi_lieu}</span>
                  </div>
                )}
                {profile.emploi_type_contrat && (
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    <span><strong>Contrat :</strong> {getTypeContratLabel(profile.emploi_type_contrat)}</span>
                  </div>
                )}
                {profile.emploi_date_debut && (
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    <span><strong>Début :</strong> {new Date(profile.emploi_date_debut).toLocaleDateString('fr-FR')}</span>
                  </div>
                )}
              </div>
              <Button variant="outline" size="sm" className="mt-3" onClick={() => setShowStatutDialog(true)}>
                Modifier
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Historique statuts */}
      {profile?.historique_statuts?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Historique des statuts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {[...profile.historique_statuts].reverse().slice(0, 10).map((entry, idx) => {
                const info = getStatutInfo(entry.statut);
                return (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: info.color }} />
                    <div className="flex-1">
                      <p className="font-medium text-sm">{info.label}</p>
                      {entry.structure && (
                        <p className="text-xs text-slate-500">
                          {entry.structure} - {entry.lieu}
                          {entry.type_contrat && ` (${getTypeContratLabel(entry.type_contrat)})`}
                        </p>
                      )}
                    </div>
                    <p className="text-xs text-slate-400">
                      {new Date(entry.date).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dialog Statut Emploi */}
      <Dialog open={showStatutDialog} onOpenChange={setShowStatutDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {statutData.statut === 'autre_emploi' ? "Détails de l'autre emploi" : "Détails de l'emploi"}
            </DialogTitle>
            <DialogDescription>Renseignez les informations sur votre emploi</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Structure / Entreprise *</Label>
              <Input value={statutData.structure} onChange={(e) => setStatutData({...statutData, structure: e.target.value})} placeholder="Nom de la structure..." />
            </div>
            <div>
              <Label>Lieu *</Label>
              <Input value={statutData.lieu} onChange={(e) => setStatutData({...statutData, lieu: e.target.value})} placeholder="Ville..." />
            </div>
            <div>
              <Label>Type de contrat *</Label>
              <select value={statutData.type_contrat} onChange={(e) => setStatutData({...statutData, type_contrat: e.target.value})} className="w-full h-10 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]">
                <option value="">Sélectionner...</option>
                {TYPES_CONTRAT.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date de début *</Label>
                <Input type="date" value={statutData.date_debut} onChange={(e) => setStatutData({...statutData, date_debut: e.target.value})} />
              </div>
              <div>
                <Label>Date de fin</Label>
                <Input type="date" value={statutData.date_fin} onChange={(e) => setStatutData({...statutData, date_fin: e.target.value})} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatutDialog(false)}>Annuler</Button>
            <Button onClick={handleUpdateStatut} disabled={saving || !statutData.structure || !statutData.lieu || !statutData.type_contrat || !statutData.date_debut} className="bg-[#8DC63F] hover:bg-[#7AB62F]">
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Ajout Document */}
      <Dialog open={showDocumentDialog} onOpenChange={setShowDocumentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter un document</DialogTitle>
            <DialogDescription>Taille maximale : 1 Mo</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nom du document</Label>
              <Input value={documentData.nom} onChange={(e) => setDocumentData({...documentData, nom: e.target.value})} placeholder="Ex: Attestation de formation..." />
            </div>
            <div>
              <Label>Fichier</Label>
              <Input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={handleDocumentFileSelect} />
              {documentData.fichier && <p className="text-xs text-green-600 mt-1">Fichier sélectionné</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDocumentDialog(false)}>Annuler</Button>
            <Button onClick={handleAddDocument} disabled={saving || !documentData.nom || !documentData.fichier} className="bg-[#8DC63F] hover:bg-[#7AB62F]">
              {saving ? 'Ajout...' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Annonces */}
      <Dialog open={showAnnoncesDialog} onOpenChange={setShowAnnoncesDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSearch className="h-5 w-5 text-blue-600" />
              Offres d'emploi disponibles
            </DialogTitle>
            <DialogDescription>Consultez les annonces et postulez directement</DialogDescription>
          </DialogHeader>
          {loadingAnnonces ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin text-[#8DC63F]" />
            </div>
          ) : annonces.length === 0 ? (
            <div className="text-center py-8">
              <FileSearch className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Aucune offre disponible pour le moment</p>
            </div>
          ) : selectedAnnonce ? (
            <div className="space-y-4">
              <Button variant="ghost" size="sm" onClick={() => setSelectedAnnonce(null)}>
                ← Retour aux annonces
              </Button>
              <div className="p-4 border border-slate-200 rounded-lg">
                <h3 className="text-lg font-semibold">{selectedAnnonce.titre}</h3>
                <div className="flex items-center gap-4 mt-2 text-sm text-slate-600">
                  <span className="flex items-center gap-1"><Building className="h-4 w-4" />{selectedAnnonce.entreprise_nom}</span>
                  <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{selectedAnnonce.lieu}</span>
                  <Badge variant="outline">{selectedAnnonce.type_contrat}</Badge>
                </div>
                <p className="mt-4 text-slate-700 whitespace-pre-wrap">{selectedAnnonce.description}</p>
              </div>
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-medium text-blue-800 mb-3">Postuler</h4>
                <div>
                  <Label>Message (optionnel)</Label>
                  <textarea value={candidatureComment} onChange={(e) => setCandidatureComment(e.target.value)} placeholder="Présentez-vous..." className="w-full min-h-[100px] mt-1 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y" />
                </div>
                <Button onClick={handlePostuler} disabled={sendingCandidature} className="mt-4 w-full bg-blue-600 hover:bg-blue-700">
                  {sendingCandidature ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Envoi...</> : <><Send className="h-4 w-4 mr-2" />Envoyer ma candidature</>}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {annonces.map(annonce => (
                <div key={annonce.offre_id} className="p-4 border border-slate-200 rounded-lg hover:border-blue-300 hover:bg-blue-50/50 transition-colors cursor-pointer" onClick={() => setSelectedAnnonce(annonce)}>
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold">{annonce.titre}</h3>
                      <div className="flex items-center gap-3 mt-1 text-sm text-slate-600">
                        <span className="flex items-center gap-1"><Building className="h-4 w-4" />{annonce.entreprise_nom}</span>
                        <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{annonce.lieu}</span>
                      </div>
                    </div>
                    <Badge variant="outline">{annonce.type_contrat}</Badge>
                  </div>
                  <p className="mt-2 text-sm text-slate-600 line-clamp-2">{annonce.description}</p>
                  {annonce.deja_postule && (
                    <div className="mt-2 flex items-center gap-1 text-sm text-green-600">
                      <CheckCircle className="h-4 w-4" />Candidature envoyée
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardMoniteur;
